<script setup lang="ts">
  import handleAppStateChanges from '@/handleAppStateChanges'
  import StoreManager from '@/classes/manager/StoreManager'
  import DatabaseManager from '@/classes/manager/DatabaseManager'
  import PushNotificationManager from '@/classes/manager/PushNotificationManager'
  import { navigateToStartPage } from '@/router/actions/navigateToStartPage'
  import { useOpenApiConfiguration } from '@/composables/OpenApiComposable'
  import { useConfigStore } from '@/stores/ConfigStore'
  import { TrackingType } from '@/api/client'
  import { useLocationStore } from '@/stores/LocationStore'
  import { useAuthStore } from '@/stores/AuthStore'
  import { getUserStores, getDeviceStores, getUserStoresToSync } from '@/stores'
  import * as ExceptionReportingManager from '@/classes/manager/ExceptionReportingManager'
  import { App } from '@capacitor/app'
  import { Network } from '@capacitor/network'
  import { storeToRefs } from 'pinia'
  import { useEmployeeStore } from './stores/EmployeeStore'
  import { useEventStore } from './stores/EventStore'
  import { wait } from './helpers/wait'
  import { Preferences } from '@capacitor/preferences'
  import router from '@/router'

  /*
        This code executes when the app is started or the webview is reloaded.
      */
  await App.removeAllListeners()
  await Network.removeAllListeners()
  const configStore = useConfigStore()
  const authStore = useAuthStore()
  const employeeStore = useEmployeeStore()

  const { employee } = storeToRefs(employeeStore)

  ExceptionReportingManager.watchConfig(authStore, configStore)
  await DatabaseManager.setup()

  // Migrate existing store data from Preferences API to SQLite
  const allStores = [authStore, ...getDeviceStores(), ...getUserStores()]
  await StoreManager.migrateFromPreferencesToSQLite(allStores)

  await StoreManager.hydrate([authStore])
  useOpenApiConfiguration()
  await StoreManager.hydrate([...getDeviceStores(), ...getUserStores()])

  StoreManager.keepPersisted([
    authStore,
    ...getDeviceStores().filter((s) => s.$id !== 'projectStore'), // projectStore persists itself
    ...getUserStores(),
  ])
  if (authStore.deviceIsAuthenticated) {
    // we are only awaiting the sync of the eventStore since it is the only one really crucial for displaying correct App-State
    // all other Stores are synced in the background
    // but we are not waiting forever... if the sync takes more than 3.6 seconds, we just continue with possibly outdated state

    const sync = useEventStore().sync()
    await Promise.race([sync, wait(3600)])

    if (authStore.isTeamDevice || authStore.isTerminalDevice) {
      void useEmployeeStore().sync()
      void StoreManager.sync(
        getDeviceStores().filter(
          (x) =>
            x.$id !== useEventStore().$id && x.$id !== useEmployeeStore().$id,
        ),
      )
    } else {
      void StoreManager.sync(
        getDeviceStores().filter((x) => x.$id !== useEventStore().$id),
      )
    }

    // Start push notifications if device is authenticated
    if (authStore.deviceIsAuthenticated && !import.meta.env.DEV) {
      void PushNotificationManager.start()
    }
  }
  if (authStore.userIsAuthenticated && employee.value) {
    // we are not awaiting the sync of the userStores since they are not crucial for displaying correct App-State
    // (myArea is never shown on appStart)
    void StoreManager.sync(getUserStoresToSync(employee.value))
  }
  await configStore.init()

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.config = configStore // this is just for testing purposes and has no effect on the app

  handleAppStateChanges()

  if (authStore.isTeamDevice) {
    if (
      employeeStore.employee &&
      employeeStore.teamleader &&
      employeeStore.employee.id !== employeeStore.teamleader.id
    ) {
      employeeStore.setActiveEmployee(employeeStore.teamleader)
    }
  }
  await navigateToStartPage()

  // make sure changes to location tracking permissions are reflected immediately
  useConfigStore().$subscribe((_, state) => {
    if (state.tracking_type !== TrackingType.INTERVAL) {
      useLocationStore().stopLoggingLocations()
    }
  })

  if (!authStore.deviceIsAuthenticated) {
    const firstAppOpen = await Preferences.get({ key: 'appWasOpened' })
    if (!firstAppOpen || !firstAppOpen.value) {
      Preferences.set({ key: 'appWasOpened', value: 'true' })
        .then(async () => {
          await authStore.attemptClipboardLogin()
        })
        .then(() => {
          if (authStore.deviceIsAuthenticated) {
            setTimeout(async () => {
              await router.replace({
                name: 'personalAppManagerNewOnboarding',
              })
            }, 0)
          }
        })
        .catch((error) => {
          console.error('Error during first app open handling:', error)
        })
    }
  }
</script>

<template>
  <slot></slot>
</template>
